import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, ArrowRight, Brain, Lightbulb, Target, Rocket } from "lucide-react";

// Helper function to format technical tension keys into human-readable labels
const formatTension = (tension: string): string => {
  // If it looks like a technical key (contains underscores), format it
  if (tension.includes("_vs_") || tension.includes("_")) {
    // Known tension mappings
    const tensionLabels: Record<string, string> = {
      "wertankerung_vs_adoption": "Wertmonetarisierung vs. schnelle Nutzeradoption",
      "growth_vs_profitability": "Wachstum vs. Profitabilität",
      "speed_vs_quality": "Geschwindigkeit vs. Qualität",
      "innovation_vs_stability": "Innovation vs. Stabilität",
      "short_term_vs_long_term": "Kurzfristige vs. langfristige Ziele",
      "cost_vs_value": "Kosten vs. Wertschöpfung",
      "risk_vs_opportunity": "Risiko vs. Chance",
      "control_vs_flexibility": "Kontrolle vs. Flexibilität",
    };
    
    // Check for exact match first
    if (tensionLabels[tension.toLowerCase()]) {
      return tensionLabels[tension.toLowerCase()];
    }
    
    // Otherwise, try to make it readable by replacing underscores and capitalizing
    return tension
      .replace(/_vs_/gi, " vs. ")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  
  // Already readable, return as-is
  return tension;
};

interface SubphaseTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromSubphase: string;
  toSubphase: string;
  detectedTensions: string[];
  problemStatement: string;
  phase?: number;
  nextPhase?: number;
}

export const SubphaseTransitionDialog = ({
  open,
  onOpenChange,
  fromSubphase,
  toSubphase,
  detectedTensions,
  problemStatement,
  phase = 1,
  nextPhase = 1,
}: SubphaseTransitionDialogProps) => {
  const getTransitionIcon = () => {
    if (fromSubphase === "1.1" && toSubphase === "1.2") {
      return <Zap className="h-5 w-5 text-primary" />;
    }
    if (fromSubphase === "1.2" && toSubphase === "1.3") {
      return <Lightbulb className="h-5 w-5 text-primary" />;
    }
    if (fromSubphase === "1.3" && toSubphase === "1.4") {
      return <Target className="h-5 w-5 text-primary" />;
    }
    if (phase === 1 && nextPhase === 2) {
      return <Rocket className="h-5 w-5 text-primary" />;
    }
    return <Brain className="h-5 w-5 text-primary" />;
  };

  const getTransitionTitle = () => {
    if (fromSubphase === "1.1" && toSubphase === "1.2") {
      return "Problem verstanden";
    }
    if (fromSubphase === "1.2" && toSubphase === "1.3") {
      return "Spannungsfeld identifiziert";
    }
    if (fromSubphase === "1.3" && toSubphase === "1.4") {
      return "Hypothesen formuliert";
    }
    if (fromSubphase === "1.4" && toSubphase === "2.0") {
      return "Verdichtung abgeschlossen";
    }
    if (phase === 1 && nextPhase === 2) {
      return "Phase 1 abgeschlossen";
    }
    return "Übergang zur nächsten Phase";
  };

  const getTransitionDescription = () => {
    if (fromSubphase === "1.1" && toSubphase === "1.2") {
      return "Du hast den Kern deiner Entscheidung herausgearbeitet. Jetzt schauen wir uns die Spannungen und Zielkonflikte an, die dahinter stecken.";
    }
    if (fromSubphase === "1.2" && toSubphase === "1.3") {
      return "Du hast die zentralen Spannungen deiner Entscheidung herausgearbeitet. Jetzt geht es darum, die dahinterliegenden Annahmen zu hinterfragen.";
    }
    if (fromSubphase === "1.3" && toSubphase === "1.4") {
      return "Du hast deine Hypothesen formuliert. Jetzt konkretisieren wir deine Kernhypothese und bereiten die nächste Phase vor.";
    }
    if (fromSubphase === "1.4" && toSubphase === "2.0") {
      return "Dein Entscheidungsrahmen steht: Entscheidungsfrage, Kernhypothese und Erfolgskriterien sind definiert. Bereit für Phase 2!";
    }
    if (phase === 1 && nextPhase === 2) {
      return "Glückwunsch! Du hast Phase 1 erfolgreich abgeschlossen. Deine Kernhypothese ist klar formuliert. Jetzt geht es darum, sie systematisch zu testen.";
    }
    return "Weiter zur nächsten Phase.";
  };

  const getNextStepText = (): React.ReactNode => {
    if (fromSubphase === "1.1" && toSubphase === "1.2") {
      return (
        <>
          Wir untersuchen jetzt die <span className="font-semibold">Spannungsfelder und Zielkonflikte</span>, die in deiner Entscheidung eine Rolle spielen. Welche Ziele konkurrieren miteinander?
        </>
      );
    }
    if (fromSubphase === "1.2" && toSubphase === "1.3") {
      return (
        <>
          Wir untersuchen die <span className="font-semibold">Hypothesen und Annahmen</span>, die hinter deiner Entscheidung stecken. Welche impliziten Überzeugungen trägst du mit mir?
        </>
      );
    }
    if (fromSubphase === "1.3" && toSubphase === "1.4") {
      return (
        <>
          Wir formulieren jetzt deine <span className="font-semibold">Kernhypothese</span> präzise aus und validieren, ob sie testbar ist.
        </>
      );
    }
    if (fromSubphase === "1.4" && toSubphase === "2.0") {
      return (
        <>
          Phase 1 ist abgeschlossen. In <span className="font-semibold">Phase 2</span> entwickeln wir strategische Optionen für deine Entscheidung.
        </>
      );
    }
    if (phase === 1 && nextPhase === 2) {
      return (
        <>
          In Phase 2 entwickeln wir konkrete <span className="font-semibold">Tests und Experimente</span>, um deine Hypothese zu überprüfen.
        </>
      );
    }
    return "Weiter zur nächsten Phase.";
  };

  const getButtonText = () => {
    if (phase === 1 && nextPhase === 2) {
      return "Weiter zu Phase 2";
    }
    return `Weiter zu Phase ${toSubphase}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-2 animate-scale-in">
            <div className="p-2 bg-primary/10 rounded-lg">
              {getTransitionIcon()}
            </div>
            <DialogTitle className="text-xl">{getTransitionTitle()}</DialogTitle>
          </div>
          <DialogDescription className="text-base animate-fade-in [animation-delay:100ms]">
            {getTransitionDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Problem-Statement entfernt - Nutzer soll denken, nicht lesen */}

          {detectedTensions.length > 0 && (fromSubphase === "1.2" || fromSubphase === "1.3") && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg animate-fade-in [animation-delay:300ms]">
              <p className="text-xs font-medium text-muted-foreground mb-3">
                {fromSubphase === "1.2" ? "Identifizierte Spannungsfelder:" : "Deine Hypothesen:"}
              </p>
              <div className="space-y-2">
                {detectedTensions.map((tension, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-sm text-foreground"
                  >
                    <span className="text-primary font-medium">•</span>
                    <span>{formatTension(tension)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg animate-fade-in [animation-delay:400ms]">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Als Nächstes:
            </p>
            <p className="text-sm text-foreground">
              {getNextStepText()}
            </p>
          </div>
        </div>

        <div className="flex justify-end flex-shrink-0 pt-4 border-t border-border/40 animate-fade-in [animation-delay:500ms]">
          <Button onClick={() => onOpenChange(false)} className="gap-2">
            {getButtonText()}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
